import { t } from "i18next";
import { enqueueSnackbar } from "notistack";

const DEFAULT_MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

export type ValidatedFileUploadOptions = {
  /** Max file size in bytes. Default 10MB. */
  maxFileSize?: number;
  /** i18n key prefix for error messages (invalidFileType, fileTooLarge, uploadError). Default "upload". */
  translationKeyPrefix?: string;
};

/**
 * Returns an async file upload handler that validates extension, size and non-empty file,
 * then calls onValid with the first file on success.
 * Use for DropSignedFile onFileUpload in CertificateUploadForm, SignatureMethod, TrustedProfileMethod.
 */
export const createValidatedFileUpload =
  (
    validExtensions: string[],
    onValid: (file: File) => void | Promise<unknown>,
    options: ValidatedFileUploadOptions = {}
  ): ((files: File[]) => Promise<void>) =>
  async (files: File[]) => {
    if (!files?.length) return;

    const file = files[0];
    const extension = file.name.split(".").pop()?.toLowerCase();
    const { maxFileSize = DEFAULT_MAX_FILE_SIZE, translationKeyPrefix = "upload" } = options;
    const key = (s: string) => `${translationKeyPrefix}.${s}`;

    if (!extension || !validExtensions.includes(extension)) {
      enqueueSnackbar(
        t(key("invalidFileType"), {
          fileName: file.name,
          validTypes: validExtensions.join(", "),
        }),
        { variant: "error" }
      );
      return;
    }

    if (file.size > maxFileSize) {
      enqueueSnackbar(t(key("fileTooLarge"), { fileName: file.name }), { variant: "error" });
      return;
    }

    if (file.size === 0) {
      enqueueSnackbar(t(key("uploadError")), { variant: "error" });
      return;
    }

    await onValid(file);
  };
